import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { type CoverageArtifactRow } from '@/features/codeCoverage/pages/coverageArtifactRows';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useCoverageFileTableRowActions } from '@/features/codeCoverage/pages/useCoverageFileTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { s } from '@/theme/tokens';
import { Box, TableCell, TableRow } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel, GridSortModel } from '@/ui/primitives';
import type { MetricThreshold, QueryResult, QueryResult as GridQueryResult } from '@flowqa/shared';

type CoverageFilesTableProps = {
  files: CoverageArtifactRow[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialData?: GridQueryResult<CoverageArtifactRow>;
  loading?: boolean;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
  onQueryMeta?: (meta: { total: number; items: CoverageArtifactRow[] } | null) => void;
};

type CoverageFileColumnKey =
  | 'fileName'
  | 'linesPct'
  | 'functionsPct'
  | 'branchesPct'
  | 'statementsPct'
  | 'details'
  | 'actions';

const coverageFileColumns: CoverageFileColumnKey[] = [
  'fileName',
  'linesPct',
  'functionsPct',
  'branchesPct',
  'statementsPct',
  'details',
  'actions',
];

const columnWidths: Record<CoverageFileColumnKey, string> = {
  fileName: '24%',
  linesPct: '11%',
  functionsPct: '11%',
  branchesPct: '11%',
  statementsPct: '11%',
  details: '24%',
  actions: '8%',
};

const columnLabels: Record<CoverageFileColumnKey, string> = {
  fileName: 'File',
  linesPct: 'Line',
  functionsPct: 'Function',
  branchesPct: 'Branch',
  statementsPct: 'Statement',
  details: getDetailLabel('details'),
  actions: getDetailLabel('actions'),
};

const skeletonColumnByKey: Record<CoverageFileColumnKey, TableV2SkeletonColumnKind> = {
  fileName: 'name',
  linesPct: 'metric',
  functionsPct: 'metric',
  branchesPct: 'metric',
  statementsPct: 'metric',
  details: 'name',
  actions: 'actions',
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const compareNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const resolveCoverageFileSorts = (initialSortModel?: GridSortModel): GridQueryInput['sorts'] => {
  const firstSort = initialSortModel?.[0];
  return [
    {
      field: firstSort?.field ?? 'createdAt',
      direction: firstSort?.sort === 'asc' ? 'asc' : 'desc',
    },
  ];
};

const sortFiles = (files: CoverageArtifactRow[], input: GridQueryInput): CoverageArtifactRow[] => {
  const firstSort = input.sorts[0] ?? { field: 'createdAt', direction: 'desc' as const };
  const factor = firstSort.direction === 'asc' ? 1 : -1;

  return [...files].sort((left, right) => {
    let result = 0;

    switch (firstSort.field) {
      case 'fileName':
        result = compareStrings(left.fileName, right.fileName);
        break;
      case 'sourceKey':
        result = compareStrings(left.sourceKey, right.sourceKey);
        break;
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'createdAt':
      default:
        result = compareDates(
          left.uploadCapturedAt ?? left.uploadCreatedAt,
          right.uploadCapturedAt ?? right.uploadCreatedAt,
        );
        break;
    }

    return result * factor;
  });
};

const isColumnVisible = (
  columnKey: CoverageFileColumnKey,
  columnVisibilityModel?: GridColumnVisibilityModel,
): boolean => columnVisibilityModel?.[columnKey] !== false;

const getVisibleColumns = (
  columnVisibilityModel?: GridColumnVisibilityModel,
): CoverageFileColumnKey[] =>
  coverageFileColumns.filter((columnKey) => isColumnVisible(columnKey, columnVisibilityModel));

const buildColgroup = (visibleColumns: CoverageFileColumnKey[]): ReactElement => (
  <colgroup>
    {visibleColumns.map((columnKey) => (
      <col key={columnKey} width={columnWidths[columnKey]} />
    ))}
  </colgroup>
);

const buildHeader = (visibleColumns: CoverageFileColumnKey[]): ReactElement => (
  <TableRow>
    {visibleColumns.map((columnKey) => (
      <TableCell key={columnKey} align={columnKey === 'actions' ? 'center' : 'left'}>
        {columnKey === 'actions' ? (
          <TableV2ActionsHeaderIcon />
        ) : (
          <TableV2HeaderLabel label={columnLabels[columnKey]} />
        )}
      </TableCell>
    ))}
  </TableRow>
);

const MetricCell = ({
  covered,
  percent,
  threshold,
  total,
}: {
  covered: number | null | undefined;
  percent: number | null | undefined;
  threshold: MetricThreshold;
  total: number | null | undefined;
}): ReactElement => (
  <CoverageMetricTableCell
    primary={formatPercent(percent)}
    secondary={formatCountPair(covered, total)}
    primaryColor={resolveCoverageMetricThresholdColor(percent, threshold)}
  />
);

const CoverageFileRow = ({
  columnVisibilityModel,
  isLastVisibleRow,
  onDeleteCoverageFile,
  onEditCoverageFile,
  onUpdateRowTagsColor,
  resolveThreshold,
  row,
  updateRowTagsColorPendingId,
}: {
  columnVisibilityModel?: GridColumnVisibilityModel;
  isLastVisibleRow: boolean;
  onDeleteCoverageFile: (row: CoverageArtifactRow) => void;
  onEditCoverageFile: (row: CoverageArtifactRow) => void;
  onUpdateRowTagsColor: (input: {
    row: { id: string; projectId: string; tags: string[] };
    tags: string[];
    colors: TagColor[];
  }) => void;
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold;
  row: CoverageArtifactRow;
  updateRowTagsColorPendingId: string | null;
}): ReactElement => {
  const visibleColumns = getVisibleColumns(columnVisibilityModel);
  const sourceLabel = row.sourceKey ?? row.uploadedBy;

  return (
    <TableRow sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !isLastVisibleRow })}>
      {visibleColumns.includes('fileName') ? (
        <TableCell>
          <TableV2NameCell primary={row.fileName} />
        </TableCell>
      ) : null}
      {visibleColumns.includes('linesPct') ? (
        <TableCell>
          <MetricCell
            covered={row.linesCovered}
            percent={row.linesPct}
            threshold={resolveThreshold('line')}
            total={row.linesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('functionsPct') ? (
        <TableCell>
          <MetricCell
            covered={row.functionsCovered}
            percent={row.functionsPct}
            threshold={resolveThreshold('function')}
            total={row.functionsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('branchesPct') ? (
        <TableCell>
          <MetricCell
            covered={row.branchesCovered}
            percent={row.branchesPct}
            threshold={resolveThreshold('branch')}
            total={row.branchesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('statementsPct') ? (
        <TableCell>
          <MetricCell
            covered={row.statementsCovered}
            percent={row.statementsPct}
            threshold={resolveThreshold('statement')}
            total={row.statementsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('details') ? (
        <TableCell>
          <Box sx={{ display: 'grid', gap: s('xs'), minWidth: 0 }}>
            <TableV2NameCell primary={row.uploadFileName ?? row.fileName} secondary={sourceLabel} />
            <TableV2DateTimeCell value={row.uploadCapturedAt ?? row.uploadCreatedAt} />
          </Box>
        </TableCell>
      ) : null}
      {visibleColumns.includes('actions') ? (
        <TableCell align="center">
          <TableActionsCell
            rowName={row.fileName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Coverage File')}
            editTagsEntityNoun="upload"
            rowTags={row.tags}
            onEdit={() => {
              onEditCoverageFile(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({
                row: {
                  id: row.uploadId,
                  projectId: row.projectId,
                  tags: row.tags,
                },
                ...input,
              });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.uploadId}
            deleteActionLabel={getEntityActionLabel('delete', 'Coverage File')}
            onDelete={() => {
              onDeleteCoverageFile(row);
            }}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export const CoverageFilesTable = ({
  files,
  queryKey,
  projectId = null,
  loading = false,
  initialSortModel,
  columnVisibilityModel,
  onQueryMeta,
}: CoverageFilesTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [editTarget, setEditTarget] = useState<CoverageArtifactRow | null>(null);
  const visibleColumns = useMemo(
    () => getVisibleColumns(columnVisibilityModel),
    [columnVisibilityModel],
  );
  const sorts = useMemo(() => resolveCoverageFileSorts(initialSortModel), [initialSortModel]);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.coverageRuns[metricKey],
    [thresholds.coverageRuns],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCoverageFileTableRowActions();
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageArtifactRow>> => {
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      const sortedFiles = sortFiles(files, input);
      return await Promise.resolve({
        items: sortedFiles.slice(startIndex, startIndex + pageSize),
        total: sortedFiles.length,
        page,
        pageSize,
      });
    },
    [files],
  );

  return (
    <>
      <TableV2QueryShell
        active
        ariaLabel="Coverage Files"
        colgroup={buildColgroup(visibleColumns)}
        emptyText={getEmptyStateMessage('entity.noneFound', { entity: 'coverageFile' })}
        entityLabel="coverage files"
        forceLoading={loading}
        header={buildHeader(visibleColumns)}
        pageSizeOptions={[10, 25, 50]}
        queryFn={queryFn}
        queryKey={queryKey}
        skeletonColumns={visibleColumns.map((columnKey) => skeletonColumnByKey[columnKey])}
        sorts={sorts}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <CoverageFileRow
            key={row.id}
            columnVisibilityModel={columnVisibilityModel}
            isLastVisibleRow={context.isLastVisibleRow}
            resolveThreshold={resolveThreshold}
            row={row}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteCoverageFile={(targetRow) => {
              onDeleteRow({
                fileName: targetRow.fileName,
                projectId: targetRow.projectId,
                uploadId: targetRow.uploadId,
              });
            }}
            onEditCoverageFile={setEditTarget}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'coverageFile' })}
        description="This coverage file upload and its associated coverage runs and parsed directory and file coverage will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'coverageFile',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Coverage File"
        entityName={editTarget?.fileName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.uploadId ?? null)}
        cascadeCopy="Tags will be applied to the uploaded coverage file and its associated coverage run."
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.uploadId ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({
            row: {
              id: editTarget.uploadId,
              projectId: editTarget.projectId,
              tags: editTarget.tags,
            },
            tags,
            colors,
          });
          setEditTarget(null);
        }}
      />
    </>
  );
};
